import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyCart } from '../preview/EmptyCart';
import { ROUTES_ID } from '../routes/constants';
import { navigate } from '../routes/router';
import { checkEmail, loginSuccess, SAGA_ACTIONS } from '../sagas/sagaActions';
import { BillingPaymentSection } from '../shared/BillingPaymentSection';
import { ContinueShopping } from '../shared/ContinueShopping';
import styles from '../shared/checkoutLayout.module.scss';
import { ErrorPage } from '../shared/ErrorPage';
import { Header } from '../shared/Header';
import { LegalNotice } from '../shared/LegalNotice';
import { LoadingOverlay } from '../shared/LoadingOverlay';
import { MailStep, MailSummary } from '../shared/Mail';
import { selectHasError } from '../store/appSelectors';
import { setForm } from '../store/billingSlice';
import {
  selectCartInitialized,
  selectCartLoading,
  selectCartProducts,
  selectPreviewTotals,
} from '../store/cartSelectors';
import { EDU_STATUS } from '../store/eduSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectCheckoutBlocked } from '../store/noticeSelectors';
import { onLogin } from '../utils/gtm';
import { ProofOfStatus, ProofOfStatusSummary } from './edu/ProofOfStatus';
import { OrderSummarySidebar } from './OrderSummarySidebar';
import ProductOverview from './ProductOverview';

function InactiveSteps({
  hasEduProduct,
  billingLocked,
}: {
  hasEduProduct: boolean;
  billingLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {hasEduProduct && (
        <section className={styles.step}>
          <h2 className={`${styles.stepTitle} ${styles.stepTitleLocked}`}>
            {t('Proof of student status')}
          </h2>
        </section>
      )}
      <section className={styles.step}>
        <h2
          className={`${styles.stepTitle} ${billingLocked ? styles.stepTitleLocked : ''}`}
        >
          {t('Billing information')}
        </h2>
      </section>
      <section className={styles.step}>
        <h2 className={`${styles.stepTitle} ${styles.stepTitleLocked}`}>
          {t('Payment Details')}
        </h2>
      </section>
    </>
  );
}

export default function Checkout() {
  const dispatch = useAppDispatch();
  const { total } = useAppSelector(selectPreviewTotals);
  const loading = useAppSelector(selectCartLoading);
  const initialized = useAppSelector(selectCartInitialized);
  const hasError = useAppSelector(selectHasError);
  const products = useAppSelector(selectCartProducts);
  const catalog = useAppSelector((state) => state.cart.catalog);
  const productsPreview = useAppSelector((state) => state.cart.productsPreview);
  const { form, errors } = useAppSelector((state) => state.billing);
  const { session, countries, localization } = useAppSelector(
    (state) => state.app,
  );
  const placing = useAppSelector((state) => state.checkout.placing);
  const { emailValidation, emailValidationError } = useAppSelector(
    (state) => state.checkout,
  );
  const { status: eduStatus, submitted: eduSubmitted } = useAppSelector(
    (state) => state.eduStatus,
  );
  const blocked = useAppSelector(selectCheckoutBlocked);
  const accountCancelled = useAppSelector(
    (state) => state.billing.info?.code === 'CANCELED',
  );
  const [email, setEmail] = useState('');

  const customerName = (
    session?.Email
      ? `${session.FirstName ?? ''} ${session.LastName ?? ''}`
      : `${form.firstName} ${form.lastName}`
  ).trim();

  const hasEduProduct = Boolean(catalog?.hasEduProduct);
  const eduNeedsProof =
    hasEduProduct && (!eduStatus || eduStatus === EDU_STATUS.EXPIRED);
  const eduRejected = hasEduProduct && eduStatus === EDU_STATUS.REJECTED;

  useEffect(() => {
    const messageHandler = (e: MessageEvent) => {
      if (e.data === 'closeSignInPopup') {
        dispatch(loginSuccess());
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [dispatch]);

  if (hasError) {
    return <ErrorPage />;
  }

  if (!initialized) {
    return (
      <div className={styles.page}>
        <Header />
        <div className={styles.fullPageLoading}>
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
        {(loading || placing) && <LoadingOverlay />}
        <div className={styles.grid}>
          <div className={styles.left}>
            <ProductOverview />

            {form.email ? (
              <>
                <MailSummary
                  email={form.email}
                  name={customerName}
                  isLoggedIn={Boolean(session?.Email)}
                  onEdit={() => {
                    if (!session?.Email) {
                      dispatch(setForm({ email: '' }));
                      return;
                    }

                    window.open(
                      `${window.ACCOUNTS}/logout?return_to=${window.ACCOUNTS}/close&email=${encodeURIComponent(email)}`,
                      'signin',
                      'menubar=0,resizable=1,width=500,height=700,left=300,top=100',
                    );
                  }}
                />
                {accountCancelled ? (
                  <InactiveSteps hasEduProduct={hasEduProduct} billingLocked />
                ) : (
                  <>
                    {hasEduProduct &&
                      (eduNeedsProof ? (
                        <ProofOfStatus />
                      ) : (
                        !eduRejected && (
                          <ProofOfStatusSummary
                            approved={eduStatus === EDU_STATUS.APPROVED}
                            underReview={!eduSubmitted}
                          />
                        )
                      ))}
                    {!eduNeedsProof && (
                      <BillingPaymentSection
                        total={total}
                        form={form}
                        fieldErrors={errors}
                        countries={countries}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <MailStep
                  email={email}
                  canContinue={
                    !blocked &&
                    ['new', 'existing'].includes(emailValidation ?? '')
                  }
                  error={emailValidationError}
                  onEmailChange={(value) => {
                    setEmail(value);
                    dispatch(checkEmail(value));
                  }}
                  onContinue={() => {
                    if (emailValidation === 'new' && hasEduProduct) {
                      window.open(
                        `${window.ACCOUNTS}/register?return_to=${window.ACCOUNTS}/close&email=${encodeURIComponent(email)}`,
                        'signin',
                        'menubar=0,resizable=1,width=500,height=700,left=300,top=100',
                      );
                    } else if (
                      emailValidation === 'existing' ||
                      hasEduProduct
                    ) {
                      window.open(
                        `${window.ACCOUNTS}/service_login?return_to=${window.ACCOUNTS}/close&email=${encodeURIComponent(email)}`,
                        'signin',
                        'menubar=0,resizable=1,width=500,height=700,left=300,top=100',
                      );
                    } else {
                      onLogin({
                        catalog,
                        productsPreview,
                        localization,
                        email,
                      });

                      dispatch(setForm({ email }));
                      dispatch({
                        type: SAGA_ACTIONS.UPDATE_PERSISTENT_CART,
                        data: { email },
                      });
                    }
                  }}
                />
                <InactiveSteps
                  hasEduProduct={hasEduProduct}
                  billingLocked={hasEduProduct}
                />
              </>
            )}

            <div className={styles.spacer} />

            <LegalNotice />
          </div>

          <OrderSummarySidebar />
        </div>

        <ContinueShopping onClick={() => dispatch(navigate(ROUTES_ID.CART))} />
      </main>
    </div>
  );
}
